import React, { useContext, useState, useEffect } from "react"; 
import { auth } from "../../firebase/firebase";
import { onAuthStateChanged, setPersistence, browserLocalPersistence } from "firebase/auth";
import { makeAuthenticatedRequest } from "../../utils/apiRequest";
import { doSignOut, isInDebugMode } from "../../firebase/auth";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]); // New state for storing locations


  // List of allowed emails
  const allowedEmails = [
    "achalanga.ayagama@Companytravels.com", "janith.wickramasooriya@Companytravels.com", 
    "chinthaka.lakshman@gmail.com", "contact.chathuranga@gmail.com", 
    "janithwickramasooriya@gmail.com", "psihasuni@gmail.com", 
    "chinthaka@evoqsystems.com", "chathuranga@evoqsystems.com", 
    "chamod.s@evoqsystems.com", "sandun.ds94@gmail.com", 
    "shenalikumarathunga2001@gmail.com", "thiliniwijesinghe2000@gmail.com", 
    "chinthaka.lakshman@gmail.com", "shuhany.hassen@Companytravels.com", 
    "leon.peiris@Companytravels.com", "nicola.ranabahu@Companytravels.com", 
    "dhanushika.jeyakirupal@Companytravels.com","kariyawasam.ck18@gmail.com","ops@Companytravels.com",
    "shenal.wijesekara@Companytravels.com","usher.crusz@Companytravels.com","india@Companytravels.com",
    "sherman.r@Companytravels.com","Dinesh.Mendis@Companytravels.com","gavin.loos@Companytravels.com","dinesh.mendis@Companytravels.com","test1@gmail.com"

  ];

  // Initialize token state
  const [token, setToken] = useState(localStorage.getItem("firebaseToken") || null);

  const fetchLocations = async (authToken) => {
    try {
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_all_locations",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (!response.ok) {
        throw new Error("Failed to fetch locations");
      }
  
      const data = await response.json();
      
      // Process locations to ensure taxAvailable property is correctly set
      const processedLocations = (data.data || []).map(location => {
        // If taxAvailable is already defined in the API response, use that value
        // Otherwise, determine it from other properties
        const taxAvailable = location.hasOwnProperty('taxAvailable') 
          ? location.taxAvailable 
          : location.hasOwnProperty('tax_info') || 
            (location.tax_percentage && location.tax_percentage > 0) || 
            location.hasOwnProperty('taxable');
        
        return {
          ...location,
          taxAvailable // Ensure taxAvailable property is set for each location
        };
      });
      
      setLocations(processedLocations);
      console.log("Processed Locations with taxAvailable:", processedLocations); // Log processed locations
    } catch (error) {
      console.error("Error fetching locations:", error.message);
      
      // If in debug mode (test1@gmail.com), show detailed error
      if (isInDebugMode()) {
        alert("Error fetching locations: " + error.message);
      }
    }
  };
  
  // Log locations when they update
  useEffect(() => {
    console.log("Updated Locations in State:", locations);
  }, [locations]);
  

  useEffect(() => {
    const configurePersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence); // Set session persistence
        console.log("Session persistence set to browserLocalPersistence");
      } catch (error) {
        console.error("Failed to set session persistence:", error.message);
      }
    };

    configurePersistence();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (!allowedEmails.includes(user.email)) {
          console.warn("Unauthorized email detected:", user.email);
          alert("Your email is not authorized to access this application.");
          await doSignOut();
          setCurrentUser(null);
          setUserLoggedIn(false);
          setToken(null);
          localStorage.removeItem("firebaseToken");
          setLoading(false);
          return;
        }

        setCurrentUser(user);
        setUserLoggedIn(true);

        try {
          const freshToken = await user.getIdToken(true);
          setToken(freshToken);
          localStorage.setItem("firebaseToken", freshToken); // Store token globally

          console.log("Token refreshed successfully:", freshToken);

          await fetchLocations(freshToken);

          console.log("Request Body:", JSON.stringify({ email: user.email }));

          // Verify the user's email using the provided endpoint
          const response = await makeAuthenticatedRequest(
            "https://us-central1-evoq-travel-craft.cloudfunctions.net/init_mail_user",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${freshToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ email: user.email }),
            }
          );

          if (!response.ok) {
            console.error("User email verification failed");
            
            // If in debug mode (test1@gmail.com), show detailed error
            if (isInDebugMode()) {
              try {
                const errorText = await response.text();
                let errorMessage = "User verification failed: " + response.status + " " + response.statusText;
                
                try {
                  // Try to parse as JSON to get more details
                  const errorJson = JSON.parse(errorText);
                  if (errorJson.error) {
                    errorMessage += "\n\nDetails: " + (errorJson.error.message || JSON.stringify(errorJson.error));
                  } else if (errorJson.message) {
                    errorMessage += "\n\nDetails: " + errorJson.message;
                  } else {
                    errorMessage += "\n\nDetails: " + JSON.stringify(errorJson);
                  }
                } catch (e) {
                  // If not JSON, just show the raw error
                  errorMessage += "\n\nDetails: " + errorText;
                }
                
                alert(errorMessage);
              } catch (parseError) {
                alert("User verification failed: " + response.status + " " + response.statusText);
              }
            } else {
              alert("Unauthorized access. Please contact admin.");
            }
            
            await doSignOut();
            setCurrentUser(null);
            setUserLoggedIn(false);
            setToken(null);
            localStorage.removeItem("firebaseToken");
          }
        } catch (error) {
          console.error("Error verifying user email:", error.message);
          
          // If in debug mode (test1@gmail.com), show detailed error
          if (isInDebugMode()) {
            alert("Error verifying user email: " + error.message);
          } else {
            alert("An error occurred during login. Please try again.");
          }
          
          await doSignOut();
          setCurrentUser(null);
          setUserLoggedIn(false);
          setToken(null);
          localStorage.removeItem("firebaseToken");
        }
      } else {
        setCurrentUser(null);
        setUserLoggedIn(false);
        setToken(null);
        localStorage.removeItem("firebaseToken");
      }
      setLoading(false);
    });

    // Refresh the token every 5 minutes
    const tokenRefreshInterval = setInterval(async () => {
      if (currentUser) {
        try {
          const freshToken = await currentUser.getIdToken(true);
          setToken(freshToken);
          localStorage.setItem("firebaseToken", freshToken); // Store the refreshed token globally
          console.log("Token refreshed after 5 minutes:", freshToken); // Log token refresh
        } catch (error) {
          console.error("Failed to refresh token:", error);
          handleLogout();
        }
      }
    }, 50 * 60 * 1000); // Refresh every 5 minutes (300,000 milliseconds)

    // Cleanup interval on component unmount
    return () => {
      clearInterval(tokenRefreshInterval);
      unsubscribe();
    };
  }, [currentUser]); // Use currentUser to determine if the token refresh logic should run

  const value = {
    currentUser,
    token,
    userLoggedIn,
    loading,
    setToken, // Expose setter for token
    setCurrentUser, // Expose setter for user
    locations
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Logout function in case of token issues
const handleLogout = async () => {
  console.warn("Logging out the user...");
  await doSignOut();
  localStorage.removeItem("firebaseToken"); // Remove token on logout
  window.location.href = "/login"; // Redirect to login page
}
